import * as THREE from 'three';
import { MASK } from '../world/Physics.js';
import { Nav } from './Nav.js';
import { Enemy, STATE } from './Enemy.js';
import { TEAM } from '../game/MatchAuthority.js';

/**
 * Squad director and pacing engine.
 *
 * Individually competent soldiers still feel like bots if they all do the same
 * thing at the same time. The director is the layer that stops that: it owns the
 * cover reservations, hands out roles (at most two attackers at once, someone
 * suppressing, occasionally a flanker), throttles spawns, and modulates the
 * squad's lethality against how the fight is actually going for the player.
 *
 * It also owns the frame budget. Perception is already time-sliced inside each
 * agent; pathfinding is queued here and solved a couple of requests per frame, so
 * eight active soldiers never spike the frame regardless of what they all decide
 * to do simultaneously.
 */

export const TUNING = {
  minAlive: 5,
  maxAlive: 8,
  spawnInterval: 2.6,
  spawnMinDistance: 26,
  spawnMaxDistance: 85,
  spawnSafeAngle: 0.2, // avoid spawning inside the player's view cone

  pathsPerFrame: 2,
  roleInterval: 1.15,
  maxPushers: 2,
  flankChance: 0.55,
  flankMinSquad: 3,

  corpseLifetime: 32,
  maxCorpses: 6,

  // pacing
  pressureRise: 0.55,
  pressureFall: 0.32,
  difficultyMin: 0.72, // aim-error multiplier when the player is winning
  difficultyMax: 2.1, // ...and when the player is nearly dead (AI misses more)

  coverSpacing: 1.7,

  /**
   * Bot skill, as multipliers on four things a soldier actually does. Selected
   * before the match; separate from `difficulty`, which is the live pacing curve
   * and keeps working on top of whichever of these is chosen.
   *
   * Four knobs rather than one, because a single "make them better" number can
   * only be aim error, and a bot that shoots straight but still takes 400 ms to
   * notice you is not a harder bot — it is the same bot that occasionally kills
   * you by surprise. Difficulty in a shooter is mostly *when* they start shooting.
   *
   *   aim       — multiplies the aim-error cone. Lower is deadlier.
   *   reaction  — multiplies the delay between acquiring you and firing. Lower is deadlier.
   *   awareness — multiplies how fast they build certainty. Higher is deadlier.
   *   sight     — multiplies sight range. Higher is deadlier.
   */
  skills: {
    recruit: { aim: 1.9, reaction: 1.7, awareness: 0.6, sight: 0.75 },
    regular: { aim: 1.0, reaction: 1.0, awareness: 1.0, sight: 1.0 },
    veteran: { aim: 0.7, reaction: 0.7, awareness: 1.35, sight: 1.15 },
    elite: { aim: 0.5, reaction: 0.45, awareness: 1.8, sight: 1.3 },
  },
  defaultSkill: 'regular',
  maxCoverPoints: 420,
  muzzleLightIntensity: 26,
};

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _c = new THREE.Vector3();
const clamp = THREE.MathUtils.clamp;

export class AIDirector {
  constructor(ctx) {
    this.ctx = ctx;
    this.events = ctx.events;
    this.physics = ctx.physics;
    this.rng = ctx.rng ?? Math.random;

    this.enemies = [];
    this.corpses = [];
    this.nav = new Nav(ctx);
    this.enabled = true;

    this.spawnPoints = [];
    this.coverPoints = [];
    this._coverByEnemy = new Map();

    this._pathQueue = [];
    this._spawnTimer = 1.5;
    this._roleTimer = 0;
    this._variant = 0;

    // Pacing
    this.pressure = 0; // 0 = player comfortable, 1 = player in trouble
    this.dominance = 0; // rises as the player racks up kills unharmed
    this.difficulty = 1;
    this.skillId = TUNING.defaultSkill;
    this.skill = TUNING.skills[this.skillId];
    this.kills = 0;
    this.playerHealth = 100;
    this._lastPlayerDamage = -99;

    this.stats = { ms: 0, peakMs: 0, enemies: 0, paths: 0, navMs: 0, navCells: 0 };
    this._msEma = 0;

    this._buildNav();
    this._collectSpawnPoints();
    this._collectCoverPoints();

    // Muzzle flashes: one shared light moved to the most recent shooter. Eight
    // real dynamic lights would cost more than the entire AI system.
    if (ctx.quality?.level !== 'medium') {
      this.muzzleLight = new THREE.PointLight(0xffcf90, 0, 14, 2);
      this.muzzleLight.castShadow = false;
      ctx.scene.add(this.muzzleLight);
      this._muzzleT = 0;
    }

    this._bind();
    this._initialSpawn();

    ctx.engine.registerUpdate((dt, elapsed) => this.update(dt, elapsed), 'ai');
  }

  /* ------------------------------------------------------------------ *
   * Setup                                                               *
   * ------------------------------------------------------------------ */

  _buildNav() {
    try {
      this.nav.build(this.ctx.level);
    } catch (err) {
      console.warn('[ai] nav build failed, agents will move directly', err);
    }
    this.stats.navMs = this.nav.buildMs;
    this.stats.navCells = this.nav.walkableCount ?? 0;
  }

  _collectSpawnPoints() {
    const level = this.ctx.level;
    const raw = level?.enemySpawns;
    let rejected = 0;
    if (Array.isArray(raw) && raw.length) {
      for (const p of raw) {
        if (!p || typeof p.x !== 'number') continue;
        const v = p.clone ? p.clone() : new THREE.Vector3(p.x, p.y, p.z);
        // A soldier who cannot reach the fight is worse than no soldier: he
        // paces a rooftop forever and the squad is quietly a man down.
        if (this.nav.ready && !this.nav.canReachMain(v)) {
          rejected++;
          continue;
        }
        this.spawnPoints.push(v);
      }
    }
    if (rejected) console.warn(`[ai] ${rejected} level spawn points are unreachable and were dropped`);
    // Fallback: scatter across the walkable grid. Level.js is authored by another
    // agent and may not expose spawns yet, so never depend on them.
    if (this.spawnPoints.length < 8 && this.nav.ready) {
      for (let i = 0; i < 40; i++) {
        const p = this.nav.randomWalkable(this.rng, null, 0, Infinity, 12);
        if (p) this.spawnPoints.push(p.clone());
      }
    }
  }

  /**
   * Cover points, preferring the level's own list. When it is absent we derive
   * them from the collider set: every box face taller than ~0.8 m is a piece of
   * cover, sampled along its length and pushed out to a standable position.
   */
  _collectCoverPoints() {
    const level = this.ctx.level;
    const raw = level?.coverPoints;
    if (Array.isArray(raw) && raw.length) {
      let i = 0;
      for (const c of raw) {
        if (!c?.position) continue;
        const pos = c.position.clone
          ? c.position.clone()
          : new THREE.Vector3(c.position.x, c.position.y, c.position.z);
        // Cover an agent cannot walk to is a trap: he paths at it forever.
        if (this.nav.ready && !this.nav.canReachMain(pos)) continue;
        this.coverPoints.push({
          position: pos,
          normal: (c.normal?.clone ? c.normal.clone() : new THREE.Vector3(0, 0, 1)).setY(0).normalize(),
          height: c.height ?? 1.2,
          claimedBy: null,
          side: i++ % 2 === 0 ? 1 : -1,
        });
      }
    }
    if (this.coverPoints.length >= 8) return;

    const cols = this.physics.colliders ?? [];
    const out = [];
    for (const c of cols) {
      if (!(c.mask & (MASK.WORLD | MASK.PROP))) continue;
      const sx = c.max.x - c.min.x;
      const sz = c.max.z - c.min.z;
      const top = c.max.y;
      const bottom = c.min.y;
      if (sx > 40 || sz > 40) continue; // floors and outer walls make poor cover
      // Something you cannot actually hide behind is not cover: stair treads,
      // kerbs and pipe runs are all thin slivers that would otherwise qualify.
      if (Math.min(sx, sz) < 0.5) continue;
      const gy = this.physics.groundHeight((c.min.x + c.max.x) * 0.5, c.min.z - 0.8, top + 0.5, 20);
      const h = top - Math.max(bottom, gy);
      if (h < 0.75 || h > 3.2) continue;

      const faces = [
        { n: new THREE.Vector3(0, 0, -1), along: 'x', fixed: c.min.z, len: sx, base: c.min.x },
        { n: new THREE.Vector3(0, 0, 1), along: 'x', fixed: c.max.z, len: sx, base: c.min.x },
        { n: new THREE.Vector3(-1, 0, 0), along: 'z', fixed: c.min.x, len: sz, base: c.min.z },
        { n: new THREE.Vector3(1, 0, 0), along: 'z', fixed: c.max.x, len: sz, base: c.min.z },
      ];
      for (const f of faces) {
        const n = Math.max(1, Math.floor(f.len / TUNING.coverSpacing));
        for (let i = 0; i < n; i++) {
          const t = (i + 0.5) / n;
          const u = f.base + f.len * t;
          const px = f.along === 'x' ? u : f.fixed + f.n.x * 0.78;
          const pz = f.along === 'x' ? f.fixed + f.n.z * 0.78 : u;
          const py = this.physics.groundHeight(px, pz, top + 1, 20);
          if (py < c.min.y - 6) continue;
          const pos = new THREE.Vector3(px, py, pz);
          if (this.nav.ready && !this.nav.isWalkable(pos)) continue;
          out.push({
            position: pos,
            // Level.js's convention: the normal points from the cover position
            // *into* the obstacle, i.e. the direction the cover protects from.
            normal: f.n.clone().negate(),
            height: Math.min(h, 2.4),
            claimedBy: null,
            side: i % 2 === 0 ? 1 : -1,
          });
        }
      }
    }

    // Thin out if the level is dense, keeping a spatially even spread.
    if (out.length > TUNING.maxCoverPoints) {
      const stride = out.length / TUNING.maxCoverPoints;
      for (let i = 0; i < TUNING.maxCoverPoints; i++) this.coverPoints.push(out[Math.floor(i * stride)]);
    } else {
      for (const p of out) this.coverPoints.push(p);
    }
  }

  _bind() {
    const ev = this.events;
    this._unbind = [
      ev.on('weapon:fire', (p) => this._onPlayerFire(p)),
      ev.on('player:footstep', () => this._onPlayerFootstep()),
      ev.on('player:health', (p) => {
        this.playerHealth = p.health;
      }),
      ev.on('player:damage', () => {
        this._lastPlayerDamage = this.ctx.engine.elapsed;
        this.dominance = Math.max(0, this.dominance - 0.5);
      }),
      ev.on('actor:death', (p) => this._onDeath(p)),
      ev.on('game:state', (p) => {
        this.enabled = p.state === 'playing';
      }),
    ];
  }

  _initialSpawn() {
    for (const [team, n] of Object.entries(this._roster())) {
      const t = Number(team);
      for (let i = 0; i < n; i++) {
        const p = this._pickSpawn(true, t);
        if (p) this._spawn(p, t);
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * Events                                                              *
   * ------------------------------------------------------------------ */

  _onPlayerFire({ origin, dir }) {
    if (!origin || !dir) return;
    for (const e of this.enemies) {
      if (e.dead) continue;
      // A gunshot is loud: everyone in range hears roughly where it came from.
      e.onSound(origin, 1);
      // Near miss -> suppression. Distance from the shot line to the chest.
      _a.set(e.position.x, e.position.y + 1.1, e.position.z).sub(origin);
      const along = _a.dot(dir);
      if (along < 0 || along > 120) continue;
      const perp = Math.sqrt(Math.max(0, _a.lengthSq() - along * along));
      if (perp < 1.7) e.onNearMiss(origin, dir, 1.1 - perp / 1.7);
    }
  }

  _onPlayerFootstep() {
    const p = this.ctx.player;
    if (!p) return;
    const loud = p.sprinting ? 0.42 : p.crouching ? 0.12 : 0.26;
    for (const e of this.enemies) {
      if (!e.dead) e.onSound(p.position, loud);
    }
  }

  _onDeath({ actor, headshot, mine, team }) {
    const i = this.enemies.indexOf(actor);
    if (i === -1) return;
    this.enemies.splice(i, 1);
    this.corpses.push(actor);
    this.releaseCover(actor);
    // Only kills that are actually the player's should feed the pacing curve, which
    // exists to answer "is this player steamrolling us" — a bot mowing down other
    // bots on the far side of the map is not evidence about the human.
    if (mine) {
      this.kills++;
      this.dominance = Math.min(1, this.dominance + 0.28);
    }

    const dist = this.ctx.player ? this.ctx.player.position.distanceTo(actor.position) : 0;
    const friendly = team === TEAM.ALPHA;
    this.events.emit('ui:killfeed', {
      // The row already reads "YOU · weapon · victim" and draws a skull for a
      // headshot, so this slot only has to name who went down.
      key: friendly ? 'feed.ally' : 'feed.enemy',
      headshot: !!headshot,
      distance: dist,
      friendly,
    });

    // The squad reacts: a nearby death spooks the survivors. Only their own side —
    // seeing an enemy drop is encouraging, not suppressing.
    for (const e of this.enemies) {
      if (e.dead || e.team !== team) continue;
      if (e.position.distanceTo(actor.position) < 18) {
        e.suppression += 0.5;
        e.onSound(actor.position, 0.8);
      }
    }
  }

  onSpotted(enemy) {
    // First contact from anyone alerts the squad to the general area, but only
    // once every few seconds — otherwise the whole squad re-converges on the
    // player every time any one of them blinks.
    const now = this.ctx.engine.elapsed;
    if (now - (this._lastAlert ?? -99) < 4) return;
    this._lastAlert = now;
    for (const e of this.enemies) {
      if (e !== enemy && !e.dead) e.onSound(enemy.lastKnown, 0.9);
    }
  }

  onEnemyFired(enemy, origin) {
    if (this.muzzleLight) {
      this.muzzleLight.position.copy(origin);
      this.muzzleLight.intensity = TUNING.muzzleLightIntensity;
      this._muzzleT = 0.045;
    }
  }

  /* ------------------------------------------------------------------ *
   * Squad services used by Enemy                                        *
   * ------------------------------------------------------------------ */

  requestPath(enemy, target) {
    if (enemy.pathPending) return;
    enemy.pathPending = true;
    this._pathQueue.push({ enemy, target: target.clone() });
    if (this._pathQueue.length > 24) this._pathQueue.shift();
  }

  _servicePaths() {
    let budget = TUNING.pathsPerFrame;
    while (budget-- > 0 && this._pathQueue.length) {
      const job = this._pathQueue.shift();
      const e = job.enemy;
      e.pathPending = false;
      if (e.dead) continue;
      const path = this.nav.ready ? this.nav.findPath(e.position, job.target) : null;
      e.path.length = 0;
      e.pathIndex = 0;
      if (path && path.length) {
        for (const p of path) e.path.push(p);
      } else {
        // No route: walk straight at it and let the capsule sweep resolve walls.
        e.path.push(job.target.clone());
      }
      this.stats.paths++;
    }
  }

  /** Pick the best unclaimed cover that actually faces the threat. */
  claimCover(enemy, threatPos) {
    if (!this.coverPoints.length) return null;
    let best = null;
    let bestScore = -Infinity;
    const searchR = 22;
    for (const c of this.coverPoints) {
      if (c.claimedBy && c.claimedBy !== enemy) continue;
      const dE = c.position.distanceTo(enemy.position);
      if (dE > searchR) continue;
      _a.subVectors(threatPos, c.position);
      const dT = _a.length();
      if (dT < 3.5) continue; // do not hide behind the player's own wall
      _a.multiplyScalar(1 / dT);
      const facing = c.normal.x * _a.x + c.normal.z * _a.z;
      if (facing < 0.25) continue; // the cover must be between us and them

      // Prefer: close to us, a sensible engagement range, squarely facing them.
      let score = facing * 7 - dE * 0.55 - Math.abs(dT - 14) * 0.22;
      // Waist-high cover is the good stuff: you can pop up and shoot over it.
      // A 3 m wall protects you but also makes you useless.
      if (c.height > 0.85 && c.height < 1.55) score += 2.0;
      else if (c.height >= 1.55) score -= 0.8;
      // Spread the squad out: penalise cover next to an already-claimed spot.
      // Only your own squad — crowding the enemy's cover is not a mistake.
      for (const other of this.enemies) {
        if (other === enemy || other.dead || !other.cover) continue;
        if (other.team !== enemy.team) continue;
        const d = other.cover.position.distanceTo(c.position);
        if (d < 5) score -= (5 - d) * 0.8;
      }
      if (score > bestScore) {
        bestScore = score;
        best = c;
      }
    }
    if (!best) return null;
    this.releaseCover(enemy);
    best.claimedBy = enemy;
    this._coverByEnemy.set(enemy, best);
    return best;
  }

  releaseCover(enemy) {
    const c = this._coverByEnemy.get(enemy);
    if (c) {
      if (c.claimedBy === enemy) c.claimedBy = null;
      this._coverByEnemy.delete(enemy);
    }
  }

  coverProtects(cover, threatPos) {
    _a.subVectors(threatPos, cover.position).setY(0);
    if (_a.lengthSq() < 1) return false;
    _a.normalize();
    return cover.normal.x * _a.x + cover.normal.z * _a.z > 0.1;
  }

  /**
   * A flank position: far around the target's flank or rear, out of its line of
   * sight, and reachable. Candidates are drawn from the nav grid so we never
   * suggest a point inside a wall.
   */
  pickFlankPoint(enemy, targetPos) {
    if (!this.nav.ready) return null;
    /**
     * Whose eyes we are trying to get out of. This said `ctx.player` — correct while
     * every soldier was flanking the same one person, and wrong the moment a
     * squadmate is the one being flanked, because the manoeuvre would then be
     * planned around a line of sight belonging to somebody else entirely.
     */
    const victim = enemy.target ?? this.ctx.player;
    // Bearing from the target to the squad's centre of mass; flank away from it.
    let cx = 0;
    let cz = 0;
    let n = 0;
    for (const e of this.enemies) {
      if (e.dead || e.team !== enemy.team) continue;
      cx += e.position.x;
      cz += e.position.z;
      n++;
    }
    if (n) {
      cx = cx / n - targetPos.x;
      cz = cz / n - targetPos.z;
    }
    const squadAngle = Math.atan2(cz, cx);
    const side = this.rng() < 0.5 ? 1 : -1;

    let best = null;
    let bestScore = -Infinity;
    for (let i = 0; i < 14; i++) {
      const ang = squadAngle + side * (1.2 + this.rng() * 1.6);
      const dist = 11 + this.rng() * 13;
      _b.set(targetPos.x + Math.cos(ang) * dist, targetPos.y, targetPos.z + Math.sin(ang) * dist);
      const p = this.nav.nearestWalkable(_b, 4, _c);
      if (!this.nav.isWalkable(p) || !this.nav.canReachMain(p)) continue;
      const dFromTarget = Math.hypot(p.x - targetPos.x, p.z - targetPos.z);
      if (dFromTarget < 7) continue;
      // Prefer positions the target cannot currently see: that is the point.
      _a.set(p.x, p.y + 1.5, p.z);
      const eye = victim
        ? _b.set(victim.position.x, victim.position.y + (victim.eye ?? 1.62), victim.position.z)
        : _b.copy(targetPos);
      const hidden = !this.physics.visible(eye, _a, null);
      const travel = enemy.position.distanceTo(p);
      const score = (hidden ? 8 : 0) - travel * 0.16 + dFromTarget * 0.1;
      if (score > bestScore) {
        bestScore = score;
        best = p.clone();
      }
    }
    return best;
  }

  /** True if a team-mate is standing in this soldier's line of fire. */
  friendlyInLine(enemy) {
    const dir = enemy.aimDir;
    const ox = enemy.position.x;
    const oz = enemy.position.z;
    const maxD = Math.min(enemy._distToTarget, 40);
    for (const o of this.enemies) {
      if (o === enemy || o.dead) continue;
      const dx = o.position.x - ox;
      const dz = o.position.z - oz;
      const along = dx * dir.x + dz * dir.z;
      if (along < 0.6 || along > maxD) continue;
      const perp = Math.abs(dx * -dir.z + dz * dir.x);
      if (perp < 0.6 + along * 0.02) return true;
    }
    return false;
  }

  /* ------------------------------------------------------------------ *
   * Pacing                                                              *
   * ------------------------------------------------------------------ */

  _updatePacing(dt) {
    const player = this.ctx.player;
    const elapsed = this.ctx.engine.elapsed;
    const health = player ? player.health / 100 : 1;
    const recentlyHurt = elapsed - this._lastPlayerDamage < 3;

    let engagedCount = 0;
    for (const e of this.enemies) if (!e.dead && e.target) engagedCount++;

    // Pressure: how hard the squad is currently leaning on the player.
    const target = clamp((1 - health) * 0.75 + (recentlyHurt ? 0.2 : 0) + engagedCount * 0.06, 0, 1);
    const rate = target > this.pressure ? TUNING.pressureRise : TUNING.pressureFall;
    this.pressure += clamp(target - this.pressure, -rate * dt, rate * dt);
    this.dominance = Math.max(0, this.dominance - dt * 0.04);

    // Back off when the player is hurting, lean in when they are dominating.
    const want =
      1 +
      this.pressure * (TUNING.difficultyMax - 1) -
      this.dominance * (1 - TUNING.difficultyMin);
    this.difficulty = clamp(THREE.MathUtils.damp(this.difficulty, want, 1.5, dt), TUNING.difficultyMin, TUNING.difficultyMax);

    // Squad size follows the same curve: fewer bodies when the player is reeling.
    this.desiredAlive = Math.round(
      THREE.MathUtils.lerp(TUNING.maxAlive, TUNING.minAlive, this.pressure)
    );
  }

  /** Hand out roles so the squad advances as a unit rather than as a queue. */
  _assignRoles() {
    const player = this.ctx.player;
    const live = this.enemies.filter((e) => !e.dead && e.target);
    if (!live.length || !player) {
      for (const e of this.enemies) if (!e.dead) e.role = 'hold';
      return;
    }
    live.sort((a, b) => a._distToTarget - b._distToTarget);

    // How pinned is the player? If several soldiers have eyes on them and they
    // are not moving much, it is time for someone to go around.
    const seeing = live.filter((e) => e.canSee).length;
    const pinned = seeing >= 2 && player.speed < 3.2;

    let pushers = 0;
    let flankers = 0;
    const maxPush = this.pressure > 0.7 ? 1 : TUNING.maxPushers;
    const wantFlank = pinned && live.length >= TUNING.flankMinSquad ? 1 : 0;

    for (const e of live) {
      if (e.state === STATE.RELOAD || e.suppression > 1) {
        e.role = 'hold';
        continue;
      }
      if (e.role === 'flank' && e.state === STATE.FLANK && flankers < wantFlank + 1) {
        flankers++;
        continue; // let an in-progress flank finish
      }
      if (flankers < wantFlank && e._distToTarget > 12 && this.rng() < TUNING.flankChance) {
        e.role = 'flank';
        flankers++;
        continue;
      }
      if (pushers < maxPush && e._distToTarget < 30) {
        e.role = 'push';
        pushers++;
        continue;
      }
      e.role = e.canSee ? 'suppress' : 'hold';
    }
    for (const e of this.enemies) {
      if (!e.dead && !e.target) e.role = 'hold';
    }
  }

  /* ------------------------------------------------------------------ *
   * Spawning                                                            *
   * ------------------------------------------------------------------ */

  /**
   * Everyone this team has to avoid materialising in front of.
   *
   * The old rule was "far from the player and outside their view cone", which is
   * the right rule with the wrong subject — it is not about the human, it is about
   * not appearing in front of someone who can shoot you. For the hostile team those
   * are the same set. For a friendly team they are the opposite one.
   */
  _threatsTo(team, out = []) {
    out.length = 0;
    const match = this.ctx.match;
    if (!match) {
      const p = this.ctx.player;
      if (p && !p.dead) out.push(p);
      return out;
    }
    for (const reg of match.actors.values()) {
      if (reg.dead || reg.team === team || reg.team === TEAM.NONE) continue;
      const a = reg.actor;
      if (a.position && typeof a.health === 'number') out.push(a);
    }
    return out;
  }

  _pickSpawn(initial = false, team = TEAM.BRAVO) {
    const pool = this.spawnPoints;
    const threats = this._threatsTo(team, this._threatScratch ?? (this._threatScratch = []));
    if (!threats.length) {
      const p = pool[Math.floor(this.rng() * pool.length)];
      return p ? p.clone() : null;
    }

    let best = null;
    let bestScore = -Infinity;
    const tries = Math.min(pool.length, 24);
    for (let i = 0; i < tries; i++) {
      const p = pool[Math.floor(this.rng() * pool.length)];
      if (!p) continue;
      // Distance to the *nearest* enemy, not to one nominated one.
      let d = Infinity;
      for (const t of threats) d = Math.min(d, p.distanceTo(t.position));
      if (d < TUNING.spawnMinDistance || d > TUNING.spawnMaxDistance) continue;
      // Never pop into existence in an enemy's field of view.
      _a.set(p.x, p.y + 1.5, p.z);
      let visible = false;
      for (const t of threats) {
        _b.set(t.position.x, t.position.y + (t.eye ?? 1.62), t.position.z);
        if (this.physics.visible(_b, _a, null)) {
          visible = true;
          break;
        }
      }
      const score = (visible ? -10 : 0) + d * 0.05 + this.rng();
      if (score > bestScore) {
        bestScore = score;
        best = p;
      }
    }
    if (!best && this.nav.ready) {
      const p = this.nav.randomWalkable(
        this.rng,
        threats[0]?.position,
        initial ? 18 : TUNING.spawnMinDistance,
        TUNING.spawnMaxDistance,
        30
      );
      if (p) return p.clone();
    }
    return best ? best.clone() : null;
  }

  _spawn(pos, team = TEAM.BRAVO) {
    const e = new Enemy(this.ctx, this, pos, this._variant++, team);
    this.enemies.push(e);
    return e;
  }

  /** How many bots each side should have on the field. The mode decides. */
  _roster() {
    const r = this.ctx.match?.rules?.roster;
    if (r) return r;
    // Skirmish: one hostile squad sized by the pacing curve, no friendlies.
    return { [TEAM.BRAVO]: this.desiredAlive ?? TUNING.minAlive, [TEAM.ALPHA]: 0 };
  }

  _aliveOn(team) {
    let n = 0;
    for (const e of this.enemies) if (!e.dead && e.team === team) n++;
    return n;
  }

  /**
   * Choose how good the bots are. Takes effect immediately on every living
   * soldier, because they read the multipliers rather than caching them.
   */
  setSkill(id) {
    const s = TUNING.skills[id];
    if (!s) return this.skillId;
    this.skillId = id;
    this.skill = s;
    this.events.emit('ai:skill', { id, ...s });
    return id;
  }

  /**
   * Turn the squad off, or back on — and mean it.
   *
   * `enabled = false` on its own is a much weaker statement than it looks: it stops
   * spawning, pacing and role assignment, and the soldiers already standing on the
   * map carry on hunting, shooting and dying. A networked match set that flag,
   * announced "bots are paused", and was measured with eleven of them still alive
   * and one of them shooting a remote human — the visitor's health draining and the
   * host's team score climbing to six with nobody firing a shot.
   *
   * Clearing the field is what the sentence actually meant.
   */
  setEnabled(on) {
    this.enabled = !!on;
    if (!on) {
      for (const e of this.enemies) e.dispose();
      for (const e of this.corpses) e.dispose();
      this.enemies.length = 0;
      this.corpses.length = 0;
      this._coverByEnemy.clear();
      for (const c of this.coverPoints) c.claimedBy = null;
    } else if (!this.enemies.length) {
      this._spawnTimer = TUNING.spawnInterval;
      this._initialSpawn();
    }
    return this.enabled;
  }

  /** Where the current mode wants this soldier's side to be, or null. */
  rallyFor(enemy) {
    return this.ctx.match?.rules?.rallyFor?.(enemy.team, enemy) ?? null;
  }

  /**
   * Clear the field and field both sides again.
   *
   * For round-based modes, which need everybody alive and back at a start
   * position rather than trickling in on the spawn timer. Corpses go too: a round
   * that begins with the last one's bodies still lying around reads as a bug even
   * though it is only a lifetime that has not expired yet.
   */
  resetRound() {
    for (const e of this.enemies) e.dispose();
    for (const e of this.corpses) e.dispose();
    this.enemies.length = 0;
    this.corpses.length = 0;
    this._coverByEnemy.clear();
    for (const c of this.coverPoints) c.claimedBy = null;
    this._spawnTimer = TUNING.spawnInterval;
    this._initialSpawn();
  }

  /** Debug helper: drop a soldier in front of the camera. */
  debugSpawn(distance = 6) {
    const cam = this.ctx.camera;
    const dir = new THREE.Vector3();
    cam.getWorldDirection(dir);
    dir.y = 0;
    dir.normalize();
    const p = cam.position.clone().addScaledVector(dir, distance);
    p.y = this.physics.groundHeight(p.x, p.z, p.y + 3, 20);
    return this._spawn(p);
  }

  /* ------------------------------------------------------------------ *
   * Frame                                                               *
   * ------------------------------------------------------------------ */

  update(dt, elapsed) {
    const t0 = performance.now();

    if (this.muzzleLight && this._muzzleT > 0) {
      this._muzzleT -= dt;
      if (this._muzzleT <= 0) this.muzzleLight.intensity = 0;
      else this.muzzleLight.intensity = TUNING.muzzleLightIntensity * (this._muzzleT / 0.045);
    }

    if (this.enabled) {
      this._updatePacing(dt);

      this._roleTimer -= dt;
      if (this._roleTimer <= 0) {
        this._roleTimer = TUNING.roleInterval;
        this._assignRoles();
      }

      this._servicePaths();

      this._spawnTimer -= dt;
      if (this._spawnTimer <= 0) {
        // Whichever side is furthest below strength gets the next body, so a team
        // that has just been wiped refills before the one that is a man down.
        let want = null;
        let worst = 0;
        for (const [team, n] of Object.entries(this._roster())) {
          const t = Number(team);
          const gap = n - this._aliveOn(t);
          if (gap > worst) {
            worst = gap;
            want = t;
          }
        }
        if (want !== null) {
          this._spawnTimer = TUNING.spawnInterval * (0.7 + this.rng() * 0.8);
          const p = this._pickSpawn(false, want);
          if (p) this._spawn(p, want);
        }
      }
    }

    // Snapshot: a death during update splices `enemies`, and mutating an array
    // you are iterating silently skips an agent.
    const live = this._tickList ?? (this._tickList = []);
    live.length = 0;
    for (const e of this.enemies) live.push(e);
    for (const e of live) e.update(dt, elapsed);

    // Corpses keep simulating until they settle, then just sit there costing a
    // draw call until they are recycled.
    for (let i = this.corpses.length - 1; i >= 0; i--) {
      const c = this.corpses[i];
      c.update(dt, elapsed);
      const tooOld = c.corpseTime > TUNING.corpseLifetime;
      const tooMany = this.corpses.length - i > TUNING.maxCorpses;
      if (tooOld || tooMany) {
        c.dispose();
        this.corpses.splice(i, 1);
      }
    }

    const ms = performance.now() - t0;
    this._msEma = this._msEma * 0.92 + ms * 0.08;
    this.stats.ms = this._msEma;
    this.stats.peakMs = Math.max(this.stats.peakMs * 0.995, ms);
    this.stats.enemies = this.enemies.length;
  }

  /** Debug summary, handy from the console via `__ctx.ai.report()`. */
  report() {
    return {
      ms: +this.stats.ms.toFixed(3),
      peakMs: +this.stats.peakMs.toFixed(3),
      alive: this.enemies.length,
      corpses: this.corpses.length,
      navMs: +(this.stats.navMs ?? 0).toFixed(1),
      navCells: this.stats.navCells,
      cover: this.coverPoints.length,
      spawns: this.spawnPoints.length,
      difficulty: +this.difficulty.toFixed(2),
      pressure: +this.pressure.toFixed(2),
      states: this.enemies.map((e) => `${e.state}/${e.role}${e.canSee ? '*' : ''}`),
    };
  }

  dispose() {
    for (const fn of this._unbind ?? []) fn();
    for (const e of this.enemies) e.dispose();
    for (const c of this.corpses) c.dispose();
    this.enemies.length = 0;
    this.corpses.length = 0;
    this.nav.dispose();
    if (this.muzzleLight) this.ctx.scene.remove(this.muzzleLight);
  }
}
